import React from "react";
import { useOfflineSync } from "../hooks/useOfflineSync";

/**
 * Fixed top banner — visible when navigator.onLine is false.
 * Disappears automatically when connectivity returns (useOfflineSync reacts
 * to the native window "online"/"offline" events).
 */
export const OFFLINE_BANNER_H = 37; // px — keep in sync with banner padding + line-height

export const OfflineBanner: React.FC = () => {
  const { isOnline, pending } = useOfflineSync();

  React.useEffect(() => {
    const root = document.documentElement;
    if (!isOnline) {
      root.style.setProperty("--offline-banner-h", `${OFFLINE_BANNER_H}px`);
    } else {
      root.style.removeProperty("--offline-banner-h");
    }
    return () => { root.style.removeProperty("--offline-banner-h"); };
  }, [isOnline]);

  if (isOnline) return null;

  return (
    <div style={styles.banner} role="status" aria-live="polite" aria-atomic="true">
      <span style={styles.dot} aria-hidden="true" />
      <span>
        You are offline — changes will sync when connected
        {pending > 0 && ` (${pending} queued)`}
      </span>
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  banner: {
    position:        "fixed",
    top:             0,
    left:            0,
    right:           0,
    zIndex:          9999,
    display:         "flex",
    alignItems:      "center",
    gap:             8,
    padding:         "8px 16px",
    backgroundColor: "#1f2937",
    color:           "#f9fafb",
    fontSize:        14,
    fontWeight:      500,
    boxShadow:       "0 2px 8px rgba(0,0,0,0.3)",
  },
  dot: {
    width:           8,
    height:          8,
    borderRadius:    "50%",
    backgroundColor: "#ef4444",
    flexShrink:      0,
  },
};
